<template>
  <b-modal modal-class="contacts-upload-modal" :title="title" :visible="state !== null" :size="state === 'CHOOSE-CONTACTS' ? 'lg' : 'md'"
    hide-footer :no-close-on-backdrop="noCloseState" :no-close-on-esc="noCloseState" @hide="() => state = null">
      <b-card-body v-if="state === 'LOADING'">
        <div class="text-center">
          <b-img v-if="uploadImage" :src="uploadImage" :alt="network"/>
          <loading-spinner class="mt-5"/>
        </div>
      </b-card-body>
      <b-card-body v-else-if="state === 'ERROR'">
        <div class="text-center">
          <h3 class="text-danger">{{ $t('general.errored') }}</h3>
          <template v-if="err.code === 'LINKEDIN_ZIP_NO_CONNECTIONS_FILE_FOUND'">
            <template v-if="isLocaleObject($t('sync-connections-modal.errors.linkedin-zip-no-connections-file-found'))">
              <p v-for="(text, i) in $t('sync-connections-modal.errors.linkedin-zip-no-connections-file-found')" :key="i">{{ text }}</p>
            </template>
            <template v-else>
              <p>{{ $t('sync-connections-modal.errors.linkedin-zip-no-connections-file-found') }}</p>
            </template>
          </template>
          <template v-else-if="err.code === 'NO_PEOPLE_FOUND'">
            <template v-if="isLocaleObject($t('sync-connections-modal.errors.no-people-found'))">
              <p v-for="(text, i) in $t('sync-connections-modal.errors.no-people-found')" :key="i">{{ text }}</p>
            </template>
            <template v-else>
              <p>{{ $t('sync-connections-modal.errors.no-people-found') }}</p>
            </template>
          </template>
          <template v-else-if="err.code === 'FILE_NOT_FOUND'">
            <p>{{ $t('sync-connections-modal.errors.file-not-found') }}</p>
          </template>
          <template v-else-if="err.code === 'UNKNOWN_FILE_TYPE'">
            <p>{{ $t('sync-connections-modal.errors.unknown-file-type') }}</p>
          </template>
          <p v-else>{{ `${err}` }}</p>
          <b-btn type="button" class="close-x my-3" :aria-label="$t('ctas.close')" @click="() => state = null">
            {{ $t('ctas.close') }}
          </b-btn>
        </div>
      </b-card-body>

      <b-card-body v-else-if="state === 'CONFIRM-CONTACTS'">
        <p class="text-center">{{ $t('sync-connections-modal.confirm-prompt', { n: totalSelected }) }}</p>
        <p class="text-center m-0">
          <b-btn class="mb-3" @click="startUpload" size="lg" variant="primary">
            {{ $t('sync-connections-modal.start-upload-cta') }}
          </b-btn>
          <br/>
          <b-btn class="text-muted" @click="state = 'CHOOSE-CONTACTS'" size="sm" variant="link">
            {{ $t('sync-connections-modal.select-contacts-cta') }}
          </b-btn>
        </p>
      </b-card-body>
      <b-card-body v-else-if="state === 'CHOOSE-CONTACTS'" class="p-0">
        <div class="d-flex flex-column flex-md-row justify-content-between align-items-center mb-3">
          <b-form-input class="mb-2" :placeholder="$t('contacts.placeholders.search-contacts')" v-model="contactFilter"/>
          <div style="width: 40%"/>
          <b-pagination class="mb-2" v-model="contactPage" :per-page="contactLimit" :total-rows="contactItems.length"/>
        </div>
        <b-table striped responsive borderless :fields="columnDefs" :items="contactItems" id="choose-contacts"
          :current-page="contactPage" :per-page="contactLimit" primary-key="i" small>
            <template v-slot:head(i)>
              <b-form-checkbox :checked="true" @change="value => toggleSelectAll(value)" :indeterminate="someSelected"/>
            </template>
            <template v-slot:cell(i)="{ value }">
              <b-form-checkbox v-model="selected[value]"/>
            </template>
        </b-table>
        <div class="d-flex justify-content-between align-items-center">
          <p class="m-0">
            {{ $t('sync-connections-modal.selected-count', { n: totalSelected }) }}
            <br/>
            {{ $t('sync-connections-modal.total-count', { n: contactItems.length }) }}
          </p>
          <b-btn class="m-0" @click="startUpload" variant="primary">
            {{ $t('sync-connections-modal.start-upload-cta') }}
          </b-btn>
        </div>
      </b-card-body>

      <b-card-body v-else-if="state === 'UPLOADING'" class="text-center">
        <b-img v-if="uploadImage" :src="uploadImage" :alt="network"/>
        <h3 v-if="network === 'LINKEDIN'">{{ $t('sync-connections-modal.uploading-progress.linkedin') }}</h3>
        <h3 v-else-if="network === 'MICROSOFT'">{{ $t('sync-connections-modal.uploading-progress.microsoft') }}</h3>
        <h3 v-else-if="network === 'GOOGLE'">{{ $t('sync-connections-modal.uploading-progress.google') }}</h3>
        <h3 v-else>{{ $t('sync-connections-modal.uploading-progress.generic') }}</h3>
        <p class="small-font">{{ $t('general.please-wait') }}</p>
        <loading-spinner class="mt-3"/>
      </b-card-body>

      <b-card-body v-else-if="state === 'FINISHED'" class="text-center">
        <b-img v-if="uploadImage" :src="uploadImage" :alt="network"/>
        <h3 v-if="network === 'LINKEDIN'">{{ $t('sync-connections-modal.uploading-finished.linkedin') }}</h3>
        <h3 v-else-if="network === 'MICROSOFT'">{{ $t('sync-connections-modal.uploading-finished.microsoft') }}</h3>
        <h3 v-else-if="network === 'GOOGLE'">{{ $t('sync-connections-modal.uploading-finished.google') }}</h3>
        <h3 v-else>{{ $t('sync-connections-modal.uploading-finished.generic') }}</h3>
        <template v-if="Array.isArray($t('sync-connections-modal.uploading-finished.description'))">
          <p class="small-font" v-for="(text, i) in $t('sync-connections-modal.uploading-finished.description')" :key="i">{{ text }}</p>
        </template>
        <template v-else>
          <p class="small-font">{{ $t('sync-connections-modal.uploading-finished.description') }}</p>
        </template>
        <b-btn @click="closeModal" variant="primary" v-if="$route.name === 'EventSyncContacts'">{{ $t('ctas.close') }}</b-btn>
        <b-btn @click="closeModal" variant="primary" v-else>{{ $t('ctas.continue') }}</b-btn>
      </b-card-body>
  </b-modal>
</template>

<script>
import _sortBy from 'lodash/sortBy';
import LoadingSpinner from '@/components/misc/LoadingSpinner';
import ContactsUploadMutation from '@/graphql/mutations/ContactsUpload.gql';
import i18nMixin from '@src/mixins/i18n';
import { chunkArrayInBytes, promiseReduceSeries } from '@/lib/utils';
import { parseCSV, parseZIP } from '@/lib/files';

const sessionStorage = 'sessionStorage' in global && global.sessionStorage ? global.sessionStorage : null;

export default {
  props: {
    network: {
      type: String,
      default: () => null,
    },
    rejectedCompanyNames: {
      type: Array,
      default: () => [],
      validate: a => a.filter(v => typeof v !== 'string').length === 0,
    },
    rejectedDomains: {
      type: Array,
      default: () => [],
      validate: a => a.filter(v => typeof v !== 'string').length === 0,
    },
  },
  components: {
    LoadingSpinner,
  },
  mixins: [
    i18nMixin,
  ],
  data: () => ({
    err: null,
    state: null,
    columnDefs: null,
    rowData: null,
    selected: {},
    contactFilter: null,
    contactPage: 1,
    contactLimit: 50,
  }),
  computed: {
    uploadImage() {
      switch (this.network) {
        case 'LINKEDIN': return '/static/images/illustrations/5.svg';
        case 'GOOGLE': return '/static/images/illustrations/21.svg';
        case 'MICROSOFT': return '/static/images/microsoft-uploads/file-uploading.svg';
        default: return null;
      }
    },
    noCloseState() {
      return ![ 'LOADING', 'ERROR' ].includes(this.state);
    },
    title() {
      switch (this.network) {
        case 'LINKEDIN': return this.$t('onboarding.sync-connections.upload-linkedin.title');
        case 'GOOGLE': return this.$t('onboarding.sync-connections.sync-google.title');
        case 'MICROSOFT': return this.$t('onboarding.sync-connections.sync-microsoft.title');
        default: return this.$t('sync-connections-modal.title');
      }
    },
    contactItems() {
      if (this.contactFilter) {
        const filter = `${this.contactFilter}`.trim().toLowerCase();
        return this.rowData.filter(({ where }) => where.includes(filter));
      } else {
        return this.rowData;
      }
    },
    someSelected() {
      return this.totalSelected !== 0 && this.totalSelected !== this.rowData.length;
    },
    totalSelected() {
      return this.selected.reduce((count, selected) => count + (selected ? 1 : 0), 0);
    },
  },
  methods: {
    askForFile() {
      const input = document.createElement('input');
      input.accept = '.csv,.zip';
      input.type = 'file';
      input.onchange = e => this.receivedFile(e);
      input.onblur = () => this.state = null;
      input.click();
    },
    async receivedFile(event) {
      try {
        const file = event.target.files[0];
        this.$assert(file && file.name, 'File not found', { code: 'FILE_NOT_FOUND' });

        this.state = 'LOADING';
        let fileContents = null;

        if (this.network === 'LINKEDIN') {
          if (file.name.substr(-4) === '.zip') {
            const zipContents = await parseZIP(file);
            this.$log.info(zipContents);

            const connectionsFile = zipContents.file('Connections.csv');
            this.$assert(connectionsFile && connectionsFile.name, 'No Connections.csv file was found', {
              code: 'LINKEDIN_ZIP_NO_CONNECTIONS_FILE_FOUND',
            });

            this.$log.info(connectionsFile.name);
            this.$log.info(await connectionsFile.async('blob'));

            fileContents = await parseCSV(await connectionsFile.async('blob'), {
              beforeFirstChunk: beforeFirstLinkedInChunk,
            });
          } else if (file.name.substr(-4) === '.csv') {
            this.$log.info(file);
            fileContents = await parseCSV(file, {
              beforeFirstChunk: beforeFirstLinkedInChunk,
            });
          } else {
            this.$assert.fail('An unknown file type was provided', { code: 'UNKNOWN_FILE_TYPE' });
          }
        }

        this.$assert(fileContents && Array.isArray(fileContents.data) &&
          fileContents.meta && Array.isArray(fileContents.meta.fields), 'No file contents were found');

        this.$log.info(fileContents);

        this.setData({
          fields: fileContents.meta.fields,
          data: fileContents.data,
        });
      } catch (err) {
        this.setError(err);
      }
    },
    setState(state) {
      this.state = state;
    },
    setData({ fields, data }) {
      try {
        if (!fields) {
          fields = data.reduce((ks, r) => ks.concat(Object.keys(r).forEach(k => ks.concat(!ks.includes(k) ? [ k ] : []))), []);
        }

        const { fields: columnDefs, data: rowData } = (() => {
          if (this.network === 'LINKEDIN') {
            return processLinkedInRows.call(this, { fields, data });
          } else {
            return processOtherRows.call(this, { fields, data });
          }
        })();

        this.rowData = rowData.map((data, i) => ({
          ...data,
          i,
          where: JSON.stringify(data).toLowerCase(),
        }));
        this.columnDefs = [
          { key: 'i', label: '' },
        ].concat(columnDefs.map((key, i) => ({
          key,
          sortable: true,
        })));
        this.selected = this.rowData.map(() => true);

        this.state = 'CONFIRM-CONTACTS';
      } catch (err) {
        this.setError(err);
      }
    },
    contactProvider({ filter: where, sortBy: order, sortDesc: orderDesc }) {
      let items = this.rowData.slice();

      if (typeof where === 'string') {
        items = items.filter(entry => Object.keys(entry).find(key => `${entry[key]}`.toLowerCase().includes(where)));
      }

      if (typeof order === 'string') {
        items = orderDesc ? _sortBy(items, order).reverse() : _sortBy(items, order);
      }

      return items;
    },
    toggleSelectAll(value) {
      this.selected = this.selected.map(() => value === true);
    },
    setError(err) {
      this.$log.error(err);
      this.state = 'ERROR';
      this.err = err;
    },
    async startUpload() {
      try {
        this.state = 'UPLOADING';

        const { network, someSelected, totalSelected } = this;

        const chunks = await chunkArrayInBytes(this.rowData.reduce((list, data, i) => {
          if (this.selected[i] === true) {
            list.push(this.mapNodeToContact(data));
          }
          return list;
        }, []), '100kb');
        this.$log.debug(chunks);

        const uploadErrors = await promiseReduceSeries(chunks, async (errors, uploads) => {
          const result = await this.$apollo.mutate({
            mutation: ContactsUploadMutation,
            variables: { uploads },
          });

          if (result && result.errors) {
            errors.concat(result.errors);
          }

          return errors;
        }, []);

        if (uploadErrors && uploadErrors.length) {
          uploadErrors.forEach(err => this.$log.error(err));
        }

        await this.$root.captureLogs((() => {
          const logs = [
            {
              eventName: 'CONTACT_NETWORK_SYNCED',
              user: { self: true },
              dataHash: { network, someSelected, totalSelected },
            },
          ];

          if (this.$root.user && this.$root.user.referrerUser) {
            logs.push({
              eventName: 'CONTACT_NETWORK_REFERRER_SYNCED',
              user: { id: this.$root.user.referrerUser.id },
              dataHash: { network, someSelected, totalSelected },
            });
          }

          return logs;
        })());

        // eslint-disable-next-line no-unused-expressions
        (sessionStorage ? sessionStorage.setItem('lastUploadedAt', (new Date()).toISOString()) : null);
        this.state = 'FINISHED';
      } catch (err) {
        this.setError(err);
      }
    },
    mapNodeToContact(row) {
      const upload = {};

      if (row['First Name']) {
        upload.firstName = row['First Name'];
      }
      if (row['Last Name']) {
        upload.lastName = row['Last Name'];
      }

      if (row['Email Address']) {
        upload.emailAddress = row['Email Address'];
      }

      if (row['Company'] || row['Position']) {
        const employmentRow = {};
        if (row['Company']) {
          employmentRow.companyName = row['Company'];
        }
        if (row['Position']) {
          employmentRow.title = row['Position'];
        }
        upload.employment = [ employmentRow ];
      }

      if (row['School'] || row['Degree']) {
        const educationRow = {};
        if (row['School']) {
          educationRow.schoolName = row['School'];
        }
        if (row['Degree']) {
          educationRow.degreeName = row['Degree'];
        }
        upload.education = [ educationRow ];
      }

      return upload;
    },
    closeModal() {
      this.state = null;
      this.$emit('finished-uploading', { network: this.network });
    },
  },
  watch: {
    // contactPage() {
    //   this.$scrollTo('#choose-contacts');
    // },
    contactFilter() {
      this.contactPage = 1;
    },
  },
};

function beforeFirstLinkedInChunk(chunk) {
  // If the CSV doesn't start with "First Name" then fix it!
  if (!`${chunk}`.startsWith('First Name,')) {
    return `${chunk}`.substr(`${chunk}`.indexOf('First Name,'));
  } else {
    return chunk;
  }
}

function processLinkedInRows({ fields, data }) {
  const rejectedCompanyNames = (this.rejectedCompanyNames || []).map(name => `${name}`.toLowerCase());
  const rejectedEmailDomains = (this.rejectedDomains || []).map(name => `${name}`.toLowerCase());

  data = data.filter(row => {
    if (!row || !row['First Name'] || !row['Last Name'] || !row['Company']) {
      // Remove records without a firstName, lastName or companyName
      return false;
    }

    if (rejectedCompanyNames.length && row['Company']) {
      const companyName = row['Company'].toLowerCase();
      if (rejectedCompanyNames.includes(companyName)) {
        return false;
      }
    }

    if (rejectedEmailDomains.length && row['Email Address']) {
      const [ , emailDomain ] = row['Email Address'].toLowerCase().split('@');
      if (rejectedEmailDomains.includes(emailDomain)) {
        return false;
      }
    }

    return true;
  });

  if (!Array.isArray(data)) {
    throw new Error('No valid LinkedIn rows were found');
  }

  if (fields.includes('Email Address')) {
    // Because LinkedIn doesn't really support Email Address, shift this column towards the end
    const cols = fields.splice(fields.indexOf('Email Address'), 1);
    fields.splice(fields.indexOf('Connected On'), 0, ...cols);
  }

  return { fields, data };
}

function processOtherRows({ fields, data }) {
  const rejectedCompanyNames = (this.rejectedCompanyNames || []).map(name => `${name}`.toLowerCase());
  const rejectedEmailDomains = (this.rejectedDomains || []).map(name => `${name}`.toLowerCase());

  data = data.filter(row => {
    const minimumFieldsCheck = (requiredFields => {
      const rowFields = row ? Object.keys(row) : [];
      return requiredFields.filter(search => {
        if (Array.isArray(search)) {
          return search.filter(f => rowFields.includes(f)).length === search.length;
        } else {
          return rowFields.includes(search);
        }
      }).length > 0;
    })([
      [ 'First Name', 'Last Name', 'Company' ],
      'Email Address',
      'Phone Number',
    ]);
    if (!row || !minimumFieldsCheck) {
      // Remove falsey rows or rows that fail the minimum rows check
      return false;
    }

    if (rejectedCompanyNames.length && row['Company']) {
      const companyName = row['Company'].toLowerCase();
      if (rejectedCompanyNames.includes(companyName)) {
        return false;
      }
    }

    if (rejectedEmailDomains.length && row['Email Address']) {
      const [ , emailDomain ] = row['Email Address'].toLowerCase().split('@');
      if (rejectedEmailDomains.includes(emailDomain)) {
        return false;
      }
    }

    return true;
  });

  if (!Array.isArray(data)) {
    throw new Error('No valid rows were found');
  }

  return { fields, data };
}
</script>



// WEBPACK FOOTER //
// src/modals/contacts-upload.vue